// Detect if the page is running inside a React Native WebView.
// Audio streaming via `new Audio(streamingUrl)` relies on HTTP range/chunked
// transfer which is not reliably supported for media elements inside React
// Native WebView. Instead we fetch() the full audio response, convert it into
// a blob URL, and play the blob URL with <audio> — which works reliably.
const isReactNativeWebView = () => typeof window !== 'undefined' && !!window.ReactNativeWebView

// Fetches the full audio from a streaming URL, returns a local blob URL.
// We force the MIME type to a WebView-compatible audio type because some
// servers return `application/octet-stream` or omit Content-Type, which causes
// `<audio>.play()` to reject with `NotSupportedError` inside RN WebView.
const AUDIO_MIME_FALLBACKS = ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/ogg']

const pickAudioMime = contentType => {
  if (!contentType) return 'audio/mpeg'
  const ct = contentType.toLowerCase()
  if (ct.includes('mpeg') || ct.includes('mp3')) return 'audio/mpeg'
  if (ct.includes('mp4') || ct.includes('aac')) return 'audio/mp4'
  if (ct.includes('wav') || ct.includes('wave')) return 'audio/wav'
  if (ct.includes('ogg') || ct.includes('opus')) return 'audio/ogg'
  if (ct.startsWith('audio/')) return contentType
  return 'audio/mpeg'
}

const fetchAudioAsArrayBuffer = async url => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`)
  }
  const buffer = await response.arrayBuffer()
  if (!buffer || buffer.byteLength === 0) {
    throw new Error('Empty audio response')
  }
  return { buffer, contentType: response.headers.get('Content-Type') || '' }
}

// Tries a list of MIME types until <audio> can play one. Returns the standard
// { audioStream, stop } shape on success, or throws the last error.
const playWithMimeFallback = async (url, {
  onPlaying, onComplete, completePayload, errorPayload, revokeOnEnd,
}) => {
  const { buffer, contentType } = await fetchAudioAsArrayBuffer(url)
  // Try the server-reported type first, then fall back to common audio types.
  const preferred = pickAudioMime(contentType)
  const mimesToTry = [preferred, ...AUDIO_MIME_FALLBACKS.filter(m => m !== preferred)]

  let lastError = null
  // eslint-disable-next-line no-restricted-syntax
  for (const mime of mimesToTry) {
    const blob = new Blob([buffer], { type: mime })
    const blobUrl = URL.createObjectURL(blob)
    const audio = new Audio(blobUrl)
    try {
      // eslint-disable-next-line no-await-in-loop
      await audio.play()
      if (typeof onPlaying === 'function') onPlaying()
      audio.addEventListener('ended', () => {
        if (typeof onComplete === 'function') onComplete(completePayload)
        if (revokeOnEnd) URL.revokeObjectURL(blobUrl)
      })
      return {
        audioStream: audio,
        stop() {
          audio.pause()
          audio.currentTime = 0
          if (revokeOnEnd) URL.revokeObjectURL(blobUrl)
        },
      }
    } catch (err) {
      lastError = err
      URL.revokeObjectURL(blobUrl)
    }
  }
  if (typeof onComplete === 'function') {
    onComplete(errorPayload ? { ...errorPayload, success: false, error: lastError } : { success: false, error: lastError })
  }
  throw lastError || new Error('No playable audio MIME type')
}

export {
  isReactNativeWebView,
  AUDIO_MIME_FALLBACKS,
  pickAudioMime,
  fetchAudioAsArrayBuffer,
  playWithMimeFallback,
}
